/**
 * CopyPermissionsModal — „Kopiuj uprawnienia do innych firm" w Centrum uprawnień (B12 ze szkolenia
 * 14.10). Jedno okno dla roli, zespołu i użytkownika — uprawnienia są per firma, a wdrożeniowiec
 * ma jednym ruchem przenieść te same ustawienia do kolejnych firm tenanta.
 *
 *   • ROLA — w każdej firmie powstaje nowa rola (gdy firma ma już taką — „… (kopia)" obok);
 *     `POST /api/role-templates/:id/copy`, jeden krok.
 *   • ZESPÓŁ / UŻYTKOWNIK — podmiot w firmie docelowej bywa AKTUALIZOWANY (ustawienia zastępowane,
 *     dostęp do firmy nadawany), więc przed zapisem okno pokazuje podgląd per firma
 *     (`POST /api/permissions/copy` z `dryRun`): co powstanie, co zostanie nadpisane, gdzie
 *     nadamy dostęp i czego nie da się skopiować. Dopiero „Kopiuj" zapisuje.
 *
 * Podsumowanie po zapisie zawsze pokazuje listę pominiętych — typ dokumentu, którego w firmie
 * docelowej nie ma, nie może przejść niezauważony.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { Alert } from '@/components/Alert';
import { Button } from '@/components/Button';
import { FormField } from '@/components/FormField';
import { Modal } from '@/components/Modal';
import { MultiSelect } from '@/components/MultiSelect';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { TEAMS_KEY } from '@/lib/use-permission-center';

export type CopySubjectKind = 'role' | 'team' | 'user';

interface CompanyOption {
  id: string;
  legalName: string;
  shortName: string | null;
}

/** Pozycja raportu pominiętych — lustro `SkippedItem` z serwera. */
interface SkippedItem {
  kind: string;
  reason: string;
  label: string | null;
  count: number;
}

/** Wynik per firma — wspólny kształt dla trzech rodzajów (pola specyficzne opcjonalne). */
interface CopyResult {
  companyId: string;
  name?: string;
  created?: boolean;
  renamed?: boolean;
  membersAdded?: number;
  accessGranted?: boolean;
  rolesAssigned?: number;
  overwrites?: boolean;
  rulesCopied: number;
  conditionRulesCopied: number;
  menuItemsCopied: number;
  skipped: SkippedItem[];
}

interface RoleCopyResponse {
  results: Array<
    Omit<CopyResult, 'name' | 'created'> & { roleId: string; roleName: string; renamed: boolean }
  >;
}

interface CopyPermissionsModalProps {
  open: boolean;
  subject: { kind: CopySubjectKind; id: string; name: string } | null;
  /** Firma, z której kopiujemy — nie ma jej na liście firm docelowych. */
  sourceCompanyId: string | null;
  companies: CompanyOption[];
  onClose: () => void;
}

const K = 'tenant.permissions.center.copyPermissions';

export function CopyPermissionsModal({
  open,
  subject,
  sourceCompanyId,
  companies,
  onClose,
}: CopyPermissionsModalProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [targets, setTargets] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<CopyResult[] | null>(null);
  const [results, setResults] = useState<CopyResult[] | null>(null);

  useEffect(() => {
    if (!open) return;
    setTargets([]);
    setError(null);
    setPreview(null);
    setResults(null);
  }, [open, subject?.id]);

  const companyName = useMemo(() => {
    const byId = new Map(companies.map((c) => [c.id, c.shortName ?? c.legalName]));
    return (id: string) => byId.get(id) ?? id;
  }, [companies]);

  const options = useMemo(
    () =>
      companies
        .filter((c) => c.id !== sourceCompanyId)
        .map((c) => ({ value: c.id, label: c.shortName ?? c.legalName })),
    [companies, sourceCompanyId],
  );

  const errorMessage = (err: unknown): string => {
    const body = isAxiosError(err)
      ? (err.response?.data as { error?: string; companyId?: string } | undefined)
      : undefined;
    if (body?.error === 'PERMISSION_DENIED' && body.companyId) {
      return t(`${K}.noPermissionIn`, { company: companyName(body.companyId) });
    }
    if (body?.error === 'NAME_TAKEN') return t(`${K}.conflict`);
    if (body?.error === 'USER_IS_SUPER_USER') return t(`${K}.superUser`);
    if (body?.error === 'SUBJECT_NOT_IN_COMPANY') return t(`${K}.notInCompany`);
    return t('tenant.permissions.center.saveError');
  };

  const run = useMutation({
    mutationFn: async (dryRun: boolean): Promise<CopyResult[]> => {
      if (subject!.kind === 'role') {
        const res = await api.post<RoleCopyResponse>(`/role-templates/${subject!.id}/copy`, {
          targetCompanyIds: targets,
        });
        return res.data.results.map((r) => ({ ...r, name: r.roleName, created: true }));
      }
      const res = await api.post<{ results: CopyResult[] }>('/permissions/copy', {
        subjectKind: subject!.kind,
        subjectId: subject!.id,
        sourceCompanyId,
        targetCompanyIds: targets,
        dryRun,
      });
      return res.data.results;
    },
    onSuccess: (data, dryRun) => {
      if (dryRun) {
        setPreview(data);
        return;
      }
      void queryClient.invalidateQueries({ queryKey: ['permissions', 'center'] });
      void queryClient.invalidateQueries({ queryKey: ['role-templates'] });
      void queryClient.invalidateQueries({ queryKey: TEAMS_KEY });
      toast.show({ variant: 'success', title: t(`${K}.done`, { count: data.length }) });
      setResults(data);
    },
    onError: (err) => setError(errorMessage(err)),
  });

  if (!subject) return null;

  const running = run.isPending;
  // Rola zawsze powstaje jako nowa — podgląd nie ma czego ostrzegać, zapis idzie od razu.
  const needsPreview = subject.kind !== 'role';
  const step: 'pick' | 'preview' | 'done' = results ? 'done' : preview ? 'preview' : 'pick';

  const submit = (dryRun: boolean) => {
    setError(null);
    run.mutate(dryRun);
  };

  const footer =
    step === 'done' ? (
      <Button onClick={onClose}>{t('common.close')}</Button>
    ) : step === 'preview' ? (
      <>
        <Button variant="secondary" onClick={() => setPreview(null)} disabled={running}>
          {t(`${K}.back`)}
        </Button>
        <Button loading={running} onClick={() => submit(false)}>
          {t(`${K}.submit`)}
        </Button>
      </>
    ) : (
      <>
        <Button variant="secondary" onClick={onClose} disabled={running}>
          {t('common.cancel')}
        </Button>
        <Button
          loading={running}
          disabled={targets.length === 0}
          onClick={() => submit(needsPreview)}
        >
          {t(needsPreview ? `${K}.next` : `${K}.submit`)}
        </Button>
      </>
    );

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      closeOnBackdrop={!running}
      closeOnEsc={!running}
      testId="copy-permissions-modal"
      title={t(`${K}.title.${subject.kind}`, { name: subject.name })}
      description={step === 'pick' ? t(`${K}.hint.${subject.kind}`) : undefined}
      footer={footer}
    >
      {step === 'pick' ? (
        <div className="flex flex-col gap-3">
          <FormField label={t(`${K}.targets`)} required>
            <MultiSelect
              value={targets}
              onChange={setTargets}
              options={options}
              disabled={running}
              placeholder={t(`${K}.targetsPlaceholder`)}
              data-testid="copy-permissions-targets"
            />
          </FormField>
          <p className="text-xs text-text-muted">{t(`${K}.existingNote.${subject.kind}`)}</p>
          {error && <Alert tone="error">{error}</Alert>}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {step === 'preview' && (
            <p className="text-sm text-text-secondary">{t(`${K}.previewIntro`)}</p>
          )}
          <ul
            className="flex flex-col gap-3"
            data-testid={
              step === 'preview' ? 'copy-permissions-preview' : 'copy-permissions-results'
            }
          >
            {(results ?? preview ?? []).map((r) => (
              <li key={r.companyId} className="rounded-lg border border-border-primary p-3">
                <p className="text-sm font-medium text-text-primary">{companyName(r.companyId)}</p>
                {outcomeLines(subject.kind, r, step === 'preview').map((line) => (
                  <p key={line.key} className="text-sm text-text-secondary">
                    {t(line.key, line.params)}
                  </p>
                ))}
                <p className="text-xs text-text-muted">
                  {t(step === 'preview' ? `${K}.countsPreview` : `${K}.counts`, {
                    rules: r.rulesCopied,
                    conditions: r.conditionRulesCopied,
                    menu: r.menuItemsCopied,
                  })}
                </p>
                {r.skipped.length > 0 && (
                  <div className="mt-2">
                    <Alert tone="warning" title={t(`${K}.skipped`)}>
                      <ul className="list-disc pl-5">
                        {r.skipped.map((s, i) => (
                          <li key={`${s.kind}-${s.reason}-${s.label ?? ''}-${i}`}>
                            {t(`${K}.skipReason.${s.reason}`, {
                              label: s.label ?? '',
                              count: s.count,
                            })}
                          </li>
                        ))}
                      </ul>
                    </Alert>
                  </div>
                )}
              </li>
            ))}
          </ul>
          {error && <Alert tone="error">{error}</Alert>}
        </div>
      )}
    </Modal>
  );
}

/**
 * Zdania opisujące, co się stanie (podgląd) albo co się stało (po zapisie) w jednej firmie.
 * Klucze są pełne i stałe, żeby bramka i18n widziała każdy z nich.
 */
function outcomeLines(
  kind: CopySubjectKind,
  r: CopyResult,
  isPreview: boolean,
): Array<{ key: string; params?: Record<string, unknown> }> {
  const lines: Array<{ key: string; params?: Record<string, unknown> }> = [];
  if (kind === 'role') {
    lines.push({
      key: r.renamed ? `${K}.role.createdRenamed` : `${K}.role.created`,
      params: { name: r.name },
    });
    return lines;
  }
  if (kind === 'team') {
    if (r.created) {
      lines.push({
        key: isPreview ? `${K}.team.willCreate` : `${K}.team.created`,
        params: { name: r.name },
      });
    } else {
      lines.push({
        key: isPreview
          ? r.overwrites
            ? `${K}.team.willReplace`
            : `${K}.team.willFill`
          : `${K}.team.updated`,
        params: { name: r.name },
      });
    }
    if ((r.membersAdded ?? 0) > 0) {
      lines.push({
        key: isPreview ? `${K}.team.willAddMembers` : `${K}.team.membersAdded`,
        params: { count: r.membersAdded },
      });
    }
    return lines;
  }
  if (r.accessGranted) {
    lines.push({
      key: isPreview ? `${K}.user.willGrantAccess` : `${K}.user.accessGranted`,
      params: { count: r.rolesAssigned ?? 0 },
    });
  } else if (isPreview) {
    lines.push({ key: r.overwrites ? `${K}.user.willReplace` : `${K}.user.willFill` });
  } else {
    lines.push({ key: `${K}.user.updated` });
  }
  return lines;
}
